import { useRef, type RefObject } from 'react';
import { ExpandIcon, Tooltip, TOOLTIP_SURFACE } from '@repo/atomic-components';
import { ToolkitTool } from '@/api';
import { renderClampedText } from '@/utils';
import { FlipCard } from './FlipCard';

interface ToolItemProps {
  tool: ToolkitTool;
  handleToolClick: (tool: ToolkitTool) => void;
  onExpand: (tool: ToolkitTool) => void;
}

export const ToolItem = ({ tool, handleToolClick, onExpand }: ToolItemProps) => {
  const frontExpandRef = useRef<HTMLButtonElement>(null);
  const backExpandRef = useRef<HTMLButtonElement>(null);

  return (
    <FlipCard
      front={
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg">
          <button
            type="button"
            onClick={() => handleToolClick(tool)}
            className="flex h-full w-full cursor-pointer flex-col items-start gap-2 overflow-hidden p-6 text-left transition-colors"
          >
            <div className="text-theme-black text-base font-semibold">{renderClampedText(tool.toolName, 2)}</div>
            <p className="text-theme-text-grey text-sm" title={tool.description}>
              {renderClampedText(tool.description, 4)}
            </p>
          </button>
          <button
            ref={frontExpandRef}
            type="button"
            aria-label="Expand prompt"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(tool);
            }}
            className="text-theme-text-grey hover:text-theme-dark-green focus:text-theme-dark-green absolute top-3 right-3 flex h-6 w-6 cursor-pointer items-center justify-center rounded border-none bg-transparent opacity-0 transition-opacity focus:opacity-100"
          >
            <ExpandIcon className="h-[14px] w-[14px]" />
          </button>
          <Tooltip
            target={frontExpandRef as RefObject<HTMLElement>}
            content="Expand prompt"
            position="top"
            className={TOOLTIP_SURFACE}
          />
        </div>
      }
      back={
        <div className="relative flex h-full w-full flex-col overflow-hidden rounded-lg p-6">
          <button
            type="button"
            tabIndex={-1}
            onClick={() => handleToolClick(tool)}
            className="flex min-h-0 flex-1 cursor-pointer flex-col text-left"
          >
            <div className="text-theme-black mb-2 text-xs font-semibold tracking-wide uppercase">Prompt</div>
            <div className="text-theme-text-grey flex-1 overflow-y-auto text-sm whitespace-pre-wrap">{tool.prompt}</div>
          </button>
          <button
            ref={backExpandRef}
            type="button"
            tabIndex={-1}
            aria-label="Expand prompt"
            onClick={(e) => {
              e.stopPropagation();
              onExpand(tool);
            }}
            className="text-theme-text-grey hover:text-theme-dark-green absolute top-3 right-3 flex h-6 w-6 cursor-pointer items-center justify-center rounded border-none bg-transparent"
          >
            <ExpandIcon className="h-[14px] w-[14px]" />
          </button>
          <Tooltip
            target={backExpandRef as RefObject<HTMLElement>}
            content="Expand prompt"
            position="top"
            className={TOOLTIP_SURFACE}
          />
        </div>
      }
    />
  );
};
